import { GamePanel } from '@/components/Layouts/Layout'
import { Box, Text, Button, Flex, Skeleton } from '@chakra-ui/react'
import { useTranslation } from 'next-i18next/pages'
import { AppModals, useAppModal } from '@/context/ModalProvider'
import { StatsRow } from '@/components/atoms/StatsRow/StatsRow'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import ShareIcon from '@/public/assets/icons/share.svg'
import Link from 'next/link'
import { Game } from 'types/types'
import { sections } from '../templates/UserActivity/sections'
import { transformUserLastWin } from '@/helpers/transformUserLastWin'
import { useQuery } from '@apollo/client'
import { USER_LAST_WINS } from '@/api/query'
import { NoResultsFallback } from '@/components/molecules/NoResultsFallback/NoResultsFallback'
import { TokenMoney } from '@ninjablaze/sdk-ts'

export const UserLastWin = ({ address }: { address: string }) => {
  const { t } = useTranslation()
  const modal = useAppModal()

  const { data, loading, error } = useQuery(USER_LAST_WINS, {
    variables: { address },
    fetchPolicy: 'no-cache',
  })

  const userLastWin = transformUserLastWin(address, data, t)

  if (error || (!loading && userLastWin === null)) {
    return (
      <Box>
        <Text fontSize={'xl'} fontWeight={700} mb={5}>
          {t('stats.last-win')}
        </Text>
        <NoResultsFallback
          styles={{
            h: 'calc(100% - 50px)',
            rounded: 'xl',
          }}
          text={t('messages.no-wins')}
        />
      </Box>
    )
  }

  if (loading) {
    return <Skeleton startColor="ninja.600" endColor="ninja.700" rounded={'xl'} h={462} />
  }

  const game = sections.find((game) => game.name === userLastWin?.game)!

  const handleShareResult = () => {
    modal.openModalNew(AppModals.ShareResultModal, { gameId: userLastWin?.id, game: game.name })
  }

  return (
    <Box>
      <Text fontSize={'xl'} fontWeight={700} mb={5}>
        {t('stats.last-win')}
      </Text>
      <Flex
        rounded={'2xl'}
        h={'calc(100% - 50px)'}
        p={6}
        bg={'ninja.600'}
        flexDir={'column'}
        justifyContent={'space-between'}
      >
        <Box>
          <Flex justifyContent={'space-between'} alignItems={'center'} mb={5}>
            <GamePanel
              wrapperStyles={{
                p: 0,
                cursor: 'auto',
                _hover: {
                  bg: 'transparent',
                },
              }}
              textStyles={{ fontSize: 'md', fontWeight: 700, color: 'white' }}
              icon={game.icon}
              title={game.title}
            />
            <FiatOrCryptoAmount
              iconSize={5}
              wrapperStyles={{
                color: 'brand.green',
              }}
              value={new TokenMoney(userLastWin?.reward, userLastWin?.denom || 'inj')}
            />
          </Flex>
          {userLastWin?.stats.map((stat: any, index: any) => (
            <StatsRow key={index} styles={{ bg: 'ninja.700' }} value={stat.value!} name={stat.title} />
          ))}
        </Box>
        <Flex flexDir={{ base: 'column', sm: 'row' }} alignItems={'stretch'} gap={3}>
          <Link href={`/${getGameLink(game.name)}/history/${userLastWin?.id}`}>
            <Button w={'full'} rounded={'xl'}>
              {t('common.show-game-details')}
            </Button>
          </Link>
          <Button
            flexBasis={{ base: 'auto', sm: '100%' }}
            onClick={handleShareResult}
            gap={2.5}
            variant={'orange'}
            rounded={'xl'}
            colorScheme="orange"
          >
            {t('common.share')} <ShareIcon />
          </Button>
        </Flex>
      </Flex>
    </Box>
  )
}

const getGameLink = (game: Game) => {
  switch (game) {
    case Game.JACKPOT:
      return 'jackpot'
    case Game.DOUBLE:
      return 'double'
    case Game.MINES:
      return 'mines'
  }
}
